import Vue from 'vue'
import store from '@/store'
import { S, G, M, A, Device } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import Cookie from 'js-cookie'
import { getEnvVar } from '@/utils'
import { AppSettings } from '@/types/appSettings'
import WebSocketProvider from '@/websocket'
import { Storage } from '@/store/user/storage'

const COOKIE_DOMAIN = getEnvVar('COOKIE_DOMAIN') as string

/* -------------------------------------------------------------------------- */

const getDefaultState = () => ({
  token: '',
  refresh: '',
  info: {
    id: 0,
    heartlineId: '',
    lastName: '',
    firstName: '',
    lastNameReading: '',
    firstNameReading: '',
    isWorker: false,
    isPatient: false,
    isPatientRelative: false,
    requireChangePassword: false,
    videoApiSecret: '',
    isGroupContract: false,
    externalServices: []
  },
  workerInfo: null,
  patientInfo: null,
  relativeInfo: null,
  sessionData: {},
  device: {
    maxWidth480px: false
  }
})

const state = (): S => getDefaultState()

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  isLogin: state => !!state.token,
  userInfo: state => state.info,
  sessionData: state => key => state.sessionData[key] || null,
  workerInfo: ({ workerInfo }) => workerInfo,
  patientInfo: ({ patientInfo }) => patientInfo,
  relativeInfo: ({ relativeInfo }) => relativeInfo,
  device: ({ device }) => device,
  token: state => ({ token: state.token, refresh: state.refresh })
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  login: (state, { token, refresh }) => {
    state.token = token
    state.refresh = refresh
    Cookie.set('jwt_access_token', token, {
      domain: COOKIE_DOMAIN,
      path: '/',
      sameSite: 'None',
      secure: true
    })
    localStorage.setItem('token', token)
    localStorage.setItem('refresh', refresh)
    Vue.$postMessage.saveSession(token, refresh)
    Vue.$api.setHeader('Authorization', 'Bearer ' + token)
  },
  logout: state => {
    WebSocketProvider.destroy()
    state.token = ''
    state.refresh = ''
    Cookie.set('jwt_access_token', '', {
      domain: COOKIE_DOMAIN,
      path: '/',
      sameSite: 'None',
      secure: true
    })
    localStorage.removeItem('token')
    localStorage.removeItem('refresh')
    Vue.$postMessage.saveSession('', '')
    Vue.$api.removeHeader('Authorization')
    store.commit('user/resetState')
  },
  updateUserInfo: (state, userInfo) => {
    state.info = userInfo
  },
  updateWorkerInfo: (state, workerInfo) => {
    state.workerInfo = workerInfo
  },
  updatePatientInfo: (state, patientInfo) => {
    state.patientInfo = patientInfo
  },
  updateRelativeInfo: (state, relativeInfo) => {
    state.relativeInfo = relativeInfo
  },
  setTokenToHeader: (state, token) => {
    Vue.$api.setHeader('Authorization', 'Bearer ' + token)
  },
  setSessionData: (state, sessionData) => {
    Object.keys(sessionData).forEach(key => {
      Vue.set(state.sessionData, key, sessionData[key])
    })
  },
  deleteSessionData: (state, key) => {
    Vue.delete(state.sessionData, key)
  },
  setDevice: (state, newState) => {
    ;(Object.keys(newState) as Array<keyof Device>).forEach(key => {
      Vue.set(state.device, key, newState[key])
    })
  },
  resetState: state => {
    const defState = getDefaultState()
    // データの更新タイミングがApp.vueなどのデータは、再取得されないので、残しておく
    delete defState.device // eslint-disable-line no-alert
    Object.keys(defState).forEach(key => {
      Vue.set(state, key, defState[key as keyof S])
    })
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  async login({ commit, dispatch }, { username, password }) {
    try {
      const res = await Vue.$api.auth.login({ username, password })
      commit('login', { token: res.access, refresh: res.refresh })
    } catch (e) {
      ;(dispatch as any)('ble/destroy', null, { root: true })
      commit('logout')
      throw e
    }
  },
  async logout({ commit, dispatch }) {
    ;(dispatch as any)('ble/destroy', null, { root: true })
    commit('logout')
  },
  async refreshToken({ commit, dispatch }) {
    try {
      const refresh = await Storage.refreshToken()
      if (!refresh) {
        ;(dispatch as any)('ble/destroy', null, { root: true })
        commit('logout')
        return
      }
      const res = await Vue.$api.auth.refresh({ refresh })
      commit('login', { token: res.access, refresh: res.refresh })
    } catch (e) {
      ;(dispatch as any)('ble/destroy', null, { root: true })
      commit('logout')
      throw e
    }
  },
  async updateUserInfo({ commit }) {
    const res = await Vue.$api.user.fetch()

    const appSettings = JSON.parse(res.appSettings) as AppSettings
    store.commit('appSettings/set', appSettings)

    delete res.appSettings
    commit('updateUserInfo', res)
  },
  async updateWorkerInfo({ commit }) {
    const res = await Vue.$api.user.privateWorker()
    commit('updateWorkerInfo', res)
  },
  async patchWorkerInfo({ commit }, params) {
    const res = await Vue.$api.user.patchPrivateWorker({ params })
    commit('updateWorkerInfo', res)
  },
  async updatePatientInfo({ commit }) {
    const res = await Vue.$api.user.privatePatient()
    commit('updatePatientInfo', res)
  },
  async patchPatientInfo({ commit }, params) {
    const res = await Vue.$api.user.patchPrivatePatient({ params })
    commit('updatePatientInfo', res)
  },
  async updateRelativeInfo({ commit }) {
    const res = await Vue.$api.user.privateRelative()
    commit('updateRelativeInfo', res)

    store.commit('onlineStatus/setOnlineStatus', {
      [res.patient.id]: {
        user: res.patient.id,
        isOnline: !!res.patient.isOnline
      }
    })
  },
  async patchRelativeInfo({ commit }, params) {
    const res = await Vue.$api.user.patchPrivateRelative({ params })
    commit('updateRelativeInfo', res)
  },
  async changePassword({ commit }, { password, currentPassword }) {
    await Vue.$api.user.changePassword({ password, currentPassword })
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
